import React, { useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Loader2, Save, X, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import {
    SCALE_REGISTRY, MEEM_SECTIONS, GDS15_ITEMS, PHQ9_ITEMS, PHQ9_SCALE,
    MAN_ITEMS, FRIED_ITEMS, CFS_LEVELS,
} from "@/lib/geriatricScales";

const BAND_TONE = (band) => {
    const b = (band || "").toLowerCase();
    if (b.includes("grave") || b.includes("frágil") || b.includes("desnutrição") || b.includes("importante"))
        return "bg-[#F9ECEC] text-[#9E2A2B] border-[#9E2A2B]";
    if (b.includes("moderad") || b.includes("risco") || b.includes("vulner") || b.includes("provável"))
        return "bg-[#FBF4E5] text-[#8A6A15] border-[#D4A574]";
    return "bg-[#EDF3E8] text-[#556B2F] border-[#556B2F]";
};

/**
 * Estrutura o preenchimento de MEEM / GDS-15 / PHQ-9 / MAN / Fried / CFS
 * dentro da AGA. Mostra um botão-card que:
 *   - se já há valor → exibe o valor + banda com destaque
 *   - se não há → convida ao preenchimento
 * Ao abrir, o dialog computa o score em tempo real via /api/calculators/{key}
 * e ao salvar:
 *   1. escreve o VALOR NUMÉRICO no campo AGA (compatível com motor de risco)
 *   2. cria um calculator_event para aparecer na Linha do Tempo
 *
 * Props:
 *   scale         — chave em SCALE_REGISTRY
 *   patientId     — necessário p/ persistir calculator_event
 *   consultationId— necessário p/ persistir calculator_event
 *   value         — número atual (do formData.data[section][key])
 *   result        — última resposta {band, interpretation, ...}  (opcional; guardada em `_scale_{key}`)
 *   onSave(number, resultObj)  — callback ao salvar
 */
export default function StructuredScaleField({
    scale, patientId, consultationId, value, result, onSave,
}) {
    const [open, setOpen] = useState(false);
    if (!SCALE_REGISTRY[scale]) return <div className="text-xs text-[#9E2A2B]">Escala desconhecida: {scale}</div>;
    const meta = SCALE_REGISTRY[scale];

    const hasValue = value !== null && value !== undefined && value !== "";
    const tone = result?.band ? BAND_TONE(result.band) : "bg-white border-[rgba(30,63,51,0.15)]";

    return (
        <div>
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid={`scale-btn-${scale}`}
                className={`w-full rounded-xl border-2 p-3 text-left transition-shadow hover:shadow-md ${tone}`}
            >
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                        <ClipboardCheck size={16} className="shrink-0" />
                        <div className="min-w-0">
                            <div className="text-xs font-semibold truncate">{meta.title}</div>
                            {hasValue ? (
                                <div className="text-[11px] mt-0.5">
                                    Valor <b>{value}</b>{result?.band ? ` · ${result.band}` : ""}
                                </div>
                            ) : (
                                <div className="text-[11px] mt-0.5 text-muted-clinical">
                                    Toque para preencher de forma estruturada
                                </div>
                            )}
                        </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-muted-clinical shrink-0">
                        {hasValue ? "editar" : "preencher"}
                    </span>
                </div>
            </button>

            {open && (
                <ScaleDialog
                    scale={scale}
                    patientId={patientId}
                    consultationId={consultationId}
                    initialValue={value}
                    initialResult={result}
                    onClose={() => setOpen(false)}
                    onSave={(numeric, res) => { onSave(numeric, res); setOpen(false); }}
                />
            )}
        </div>
    );
}


function ScaleDialog({ scale, patientId, consultationId, initialValue, initialResult, onClose, onSave }) {
    const meta = SCALE_REGISTRY[scale];
    const [state, setState] = useState(() => initialStateFor(scale));
    const [computing, setComputing] = useState(false);
    const [result, setResult] = useState(initialResult || null);

    const upd = (patch) => setState((prev) => ({ ...prev, ...patch }));

    const compute = async () => {
        setComputing(true);
        try {
            const payload = meta.buildPayload(state);
            const { data } = await api.post(`/calculators/${meta.calcKey}`, { inputs: payload });
            if (data?.result?.error) {
                toast.error(data.result.error);
            } else {
                setResult(data.result);
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setComputing(false); }
    };

    const save = async () => {
        if (!result || result.value === undefined) {
            toast.error("Calcule antes de salvar.");
            return;
        }
        // Persist event to timeline (best-effort — save AGA field even if this fails)
        try {
            if (patientId) {
                await api.post(`/patients/${patientId}/calculator-events`, {
                    calculator: meta.calcKey,
                    consultation_id: consultationId || null,
                    inputs: meta.buildPayload(state),
                    result,
                });
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Falha ao salvar evento na Linha do Tempo — AGA será salva mesmo assim.", e);
        }
        onSave(Number(result.value), result);
        toast.success(`${meta.title} salvo (${result.value} · ${result.band}).`);
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            data-testid={`scale-dialog-${scale}`}
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col border-2 border-[#A08A5C]/40">
                <div className="p-4 border-b-2 border-[#EDE0C6] flex items-center justify-between bg-gradient-to-r from-[#FBF7EF] to-white rounded-t-2xl">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-[#8A6A15] font-semibold">Escala estruturada</p>
                        <h3 className="font-display text-lg text-brand font-medium">{meta.title}</h3>
                    </div>
                    <button onClick={onClose} className="w-9 h-9 rounded-full bg-alt hover:bg-brand hover:text-white flex items-center justify-center" data-testid={`scale-close-${scale}`}>
                        <X size={16} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {scale === "meem"      && <MEEMForm state={state} upd={upd} />}
                    {scale === "gds15"     && <GDS15Form state={state} upd={upd} />}
                    {scale === "phq9"      && <PHQ9Form state={state} upd={upd} />}
                    {scale === "man_score" && <MANForm state={state} upd={upd} />}
                    {scale === "fried"     && <FriedForm state={state} upd={upd} />}
                    {scale === "cfs"       && <CFSForm state={state} upd={upd} />}
                </div>

                <div className="p-4 border-t-2 border-[#EDE0C6] bg-white rounded-b-2xl">
                    {result && (
                        <div className={`mb-3 rounded-lg border p-3 ${BAND_TONE(result.band)}`} data-testid={`scale-result-${scale}`}>
                            <div className="flex items-center gap-2 mb-1">
                                {result.suicidal_ideation ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
                                <b>{result.value}{result.unit || ""}</b>
                                <span className="text-xs font-semibold uppercase tracking-widest">{result.band}</span>
                            </div>
                            <p className="text-xs opacity-90">{result.interpretation}</p>
                        </div>
                    )}
                    <div className="flex justify-between gap-2">
                        <button onClick={onClose} className="btn-ghost text-sm" data-testid={`scale-cancel-${scale}`}>Fechar</button>
                        <div className="flex gap-2">
                            <button onClick={compute} disabled={computing} className="btn-secondary text-sm" data-testid={`scale-compute-${scale}`}>
                                {computing ? <Loader2 size={14} className="animate-spin" /> : <ClipboardCheck size={14} />} Calcular
                            </button>
                            <button onClick={save} disabled={!result} className="btn-primary text-sm" data-testid={`scale-save-${scale}`}>
                                <Save size={14} /> Salvar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


function initialStateFor(scale) {
    switch (scale) {
        case "meem":      return { schooling_years: 0 };
        case "gds15":     return { answers: Array(15).fill(null) };
        case "phq9":      return { answers: Array(9).fill(0) };
        case "man_score": return {};
        case "fried":     return {};
        case "cfs":       return { cfs_level: 4 };
        default:          return {};
    }
}


// ---------- Sub-formulários ----------
function MEEMForm({ state, upd }) {
    return (
        <>
            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <label className="label-clinical">Anos de escolaridade</label>
                    <input
                        type="number" min={0} max={30} value={state.schooling_years ?? 0}
                        onChange={(e) => upd({ schooling_years: Number(e.target.value) })}
                        className="input-strong"
                        data-testid="meem-schooling"
                    />
                </div>
            </div>
            {MEEM_SECTIONS.map((s) => (
                <div key={s.key} className="rounded-lg border border-[#EDE0C6] p-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-brand">{s.label}</div>
                            <div className="text-[11px] text-muted-clinical mt-0.5">{s.hint}</div>
                        </div>
                        <input
                            type="number" min={0} max={s.max}
                            value={state[s.key] ?? ""}
                            onChange={(e) => upd({ [s.key]: e.target.value === "" ? null : Number(e.target.value) })}
                            className="input-strong w-20 text-center font-semibold"
                            data-testid={`meem-${s.key}`}
                        />
                    </div>
                    <div className="text-[10px] text-muted-clinical mt-1 text-right">máx: {s.max}</div>
                </div>
            ))}
        </>
    );
}

function GDS15Form({ state, upd }) {
    const set = (i, val) => {
        const next = [...(state.answers || Array(15).fill(null))];
        next[i] = val;
        upd({ answers: next });
    };
    return (
        <ol className="space-y-2 list-decimal pl-5">
            {GDS15_ITEMS.map((item, i) => {
                const v = state.answers?.[i];
                return (
                    <li key={i} className="text-sm">
                        <div className="flex items-start justify-between gap-3">
                            <span className="text-brand">{item.q}</span>
                            <div className="inline-flex rounded-lg overflow-hidden border-2 border-[#A08A5C] shrink-0" data-testid={`gds-${i}`}>
                                {[{ v: item.depression, l: "Sim" }, { v: item.depression === 1 ? 0 : 1, l: "Não" }].map((opt) => (
                                    <button
                                        key={opt.l}
                                        type="button"
                                        onClick={() => set(i, opt.v)}
                                        className={`px-3 py-1 text-xs ${v === opt.v ? "bg-brand text-white" : "bg-white text-brand hover:bg-alt"}`}
                                    >
                                        {opt.l}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}

function PHQ9Form({ state, upd }) {
    const set = (i, val) => {
        const next = [...(state.answers || Array(9).fill(0))];
        next[i] = val;
        upd({ answers: next });
    };
    return (
        <ol className="space-y-2 list-decimal pl-5">
            {PHQ9_ITEMS.map((q, i) => (
                <li key={i} className="text-sm">
                    <div className="text-brand mb-1">{q}</div>
                    <div className="flex gap-1 flex-wrap" data-testid={`phq-${i}`}>
                        {PHQ9_SCALE.map((opt) => {
                            const active = state.answers?.[i] === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => set(i, opt.value)}
                                    className={`px-2 py-1 text-[11px] rounded-md border ${active ? "bg-brand text-white border-brand" : "bg-white text-brand border-[rgba(30,63,51,0.2)]"}`}
                                >
                                    {opt.value} — {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </li>
            ))}
        </ol>
    );
}

function MANForm({ state, upd }) {
    return (
        <div className="space-y-3">
            {MAN_ITEMS.map((item) => (
                <div key={item.key} className="rounded-lg border border-[#EDE0C6] p-3">
                    <div className="text-sm font-semibold text-brand mb-1.5">{item.label}</div>
                    <div className="flex flex-wrap gap-1.5" data-testid={`man-${item.key}`}>
                        {item.options.map((o) => {
                            const active = state[item.key] === o.value;
                            return (
                                <button
                                    key={o.value}
                                    type="button"
                                    onClick={() => upd({ [item.key]: o.value })}
                                    className={`px-3 py-1 text-xs rounded-md border ${active ? "bg-brand text-white border-brand" : "bg-white text-brand border-[rgba(30,63,51,0.2)]"}`}
                                >
                                    {o.value} — {o.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}

function FriedForm({ state, upd }) {
    return (
        <div className="space-y-2">
            {FRIED_ITEMS.map((item) => (
                <label key={item.key} className="flex items-start gap-3 p-3 rounded-lg border border-[#EDE0C6] cursor-pointer hover:bg-alt">
                    <input
                        type="checkbox"
                        checked={!!state[item.key]}
                        onChange={(e) => upd({ [item.key]: e.target.checked })}
                        className="mt-0.5 w-4 h-4"
                        data-testid={`fried-${item.key}`}
                    />
                    <span className="text-sm text-brand">{item.label}</span>
                </label>
            ))}
        </div>
    );
}

function CFSForm({ state, upd }) {
    return (
        <div className="space-y-1.5">
            {CFS_LEVELS.map((l) => {
                const active = state.cfs_level === l.value;
                return (
                    <button
                        key={l.value}
                        type="button"
                        onClick={() => upd({ cfs_level: l.value })}
                        data-testid={`cfs-${l.value}`}
                        className={`w-full text-left rounded-lg border p-2.5 transition-colors ${active ? "bg-brand text-white border-brand" : "bg-white text-brand border-[rgba(30,63,51,0.2)] hover:bg-alt"}`}
                    >
                        <div className="text-sm font-semibold">{l.label}</div>
                        <div className={`text-[11px] mt-0.5 ${active ? "text-white/90" : "text-muted-clinical"}`}>{l.desc}</div>
                    </button>
                );
            })}
        </div>
    );
}
